import { isAnnualBehaviorAiFeedbackColumnMissing } from './annualBehaviorNotesService';
/**
 * AI 피드백 저장소(record_ai_feedbacks) — 행동특성·수업기록 캐시와 같은 방식:
 * 학급(grade) 단위로 한 번에 불러와 localStorage + React Query 캐시에 두고,
 * 기록 저장/삭제 시 함께 쓰고 캐시를 즉시 갱신한다.
 * feedback.text 는 기록 메모와 같은 형식(학생은 `#m:{memberId}` 태그)이라 이름이 저장되지 않는다.
 */
import { supabase } from '../supabaseClient';
import { queryClient } from '../query/queryClient';
import { schoolKeys } from '../query/keys';
import type { AiRecordFeedbackKind } from '../utils/aiRecordFeedback';

export type RecordAiFeedbackRow = {
  annual_cell_key: string;
  kind: AiRecordFeedbackKind;
  text: string;
};

const TABLE = 'record_ai_feedbacks';
const CACHE_VERSION = 'v1';
let tableMissing = false;

function cacheKey(workspaceId: string, gradeId: string): string {
  return `cp.recordAiFeedbacks.${CACHE_VERSION}.${workspaceId}.${gradeId}`;
}

export function gradeIdFromRecordKey(annualCellKey: string): string {
  const i = (annualCellKey ?? '').indexOf('|');
  return i > 0 ? annualCellKey.slice(0, i) : '';
}

export function loadRecordAiFeedbacksCache(workspaceId: string, gradeId: string): RecordAiFeedbackRow[] | null {
  try {
    const raw = localStorage.getItem(cacheKey(workspaceId, gradeId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { rows?: RecordAiFeedbackRow[] };
    return Array.isArray(parsed?.rows) ? parsed.rows : null;
  } catch {
    return null;
  }
}

function writeCache(workspaceId: string, gradeId: string, rows: RecordAiFeedbackRow[]) {
  try {
    localStorage.setItem(cacheKey(workspaceId, gradeId), JSON.stringify({ savedAt: Date.now(), rows }));
  } catch {
    // ignore localStorage errors
  }
  queryClient.setQueryData(schoolKeys.recordAiFeedbacks(workspaceId, gradeId), rows);
}

function isMissingTable(err: { code?: string; message?: string } | null): boolean {
  if (!err) return false;
  const missing = err.code === '42P01' || err.code === 'PGRST205';
  if (missing) tableMissing = true;
  return missing;
}

/** 예전 형식({overall, students[]})도 단일 텍스트로 변환 */
function feedbackToText(raw: unknown): string {
  if (!raw || typeof raw !== 'object') return '';
  const o = raw as Record<string, unknown>;
  if (typeof o.text === 'string') return o.text;
  const lines: string[] = [];
  if (typeof o.overall === 'string' && o.overall.trim()) lines.push(o.overall.trim());
  if (Array.isArray(o.students)) {
    (o.students as Array<Record<string, unknown>>).forEach((s) => {
      if (typeof s?.memberId !== 'string') return;
      lines.push(`#m:${s.memberId} ${[s.analysis, s.guidance].filter(Boolean).join(' ')}`.trim());
    });
  }
  return lines.join('\n');
}

/** 학급 단위 일괄 조회 (React Query queryFn) */
export async function fetchRecordAiFeedbacks(workspaceId: string, gradeId: string): Promise<RecordAiFeedbackRow[]> {
  if (!workspaceId || !gradeId || tableMissing) return loadRecordAiFeedbacksCache(workspaceId, gradeId) ?? [];
  const { data, error } = await supabase
    .from(TABLE)
    .select('annual_cell_key,kind,feedback')
    .eq('workspace_id', workspaceId)
    .like('annual_cell_key', `${gradeId}|%`);
  if (error) {
    if (!isMissingTable(error)) console.warn('[ai-feedback] load failed', error.message);
    return loadRecordAiFeedbacksCache(workspaceId, gradeId) ?? [];
  }
  const rows = ((data ?? []) as Array<{ annual_cell_key: string; kind: AiRecordFeedbackKind; feedback: unknown }>)
    .map((r) => ({ annual_cell_key: r.annual_cell_key, kind: r.kind, text: feedbackToText(r.feedback) }))
    .filter((r) => r.text.trim());
  writeCache(workspaceId, gradeId, rows);
  return rows;
}

function currentRows(workspaceId: string, gradeId: string): RecordAiFeedbackRow[] {
  return (
    queryClient.getQueryData<RecordAiFeedbackRow[]>(schoolKeys.recordAiFeedbacks(workspaceId, gradeId)) ??
    loadRecordAiFeedbacksCache(workspaceId, gradeId) ??
    []
  );
}

/** 기록 저장과 함께 호출. 빈 텍스트면 삭제 */
export async function saveRecordAiFeedback(
  workspaceId: string,
  annualCellKey: string,
  kind: AiRecordFeedbackKind,
  text: string
): Promise<void> {
  const gradeId = gradeIdFromRecordKey(annualCellKey);
  if (!workspaceId || !gradeId) return;
  if (!text.trim()) {
    await deleteRecordAiFeedback(workspaceId, annualCellKey, [kind]);
    return;
  }
  const next = currentRows(workspaceId, gradeId).filter(
    (r) => !(r.annual_cell_key === annualCellKey && r.kind === kind)
  );
  next.push({ annual_cell_key: annualCellKey, kind, text });
  writeCache(workspaceId, gradeId, next);
  if (tableMissing) return;
  const { error } = await supabase.from(TABLE).upsert(
    {
      workspace_id: workspaceId,
      annual_cell_key: annualCellKey,
      kind,
      feedback: { text, savedAt: new Date().toISOString() },
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'workspace_id,annual_cell_key,kind' }
  );
  if (error && !isMissingTable(error)) console.warn('[ai-feedback] save failed', error.message);
}

/** 기록 삭제와 함께 호출 */
export async function deleteRecordAiFeedback(
  workspaceId: string,
  annualCellKey: string,
  kinds: AiRecordFeedbackKind[]
): Promise<void> {
  const gradeId = gradeIdFromRecordKey(annualCellKey);
  if (!workspaceId || !gradeId) return;
  writeCache(
    workspaceId,
    gradeId,
    currentRows(workspaceId, gradeId).filter((r) => !(r.annual_cell_key === annualCellKey && kinds.includes(r.kind)))
  );
  if (tableMissing) return;
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('annual_cell_key', annualCellKey)
    .in('kind', kinds);
  if (error && !isMissingTable(error)) console.warn('[ai-feedback] delete failed', error.message);
}

/**
 * 행동특성 저장 성공 후 처리. ai_feedback 컬럼이 있으면 레거시 행 정리(commit),
 * 컬럼 미적용 DB 면 피드백이 유실되지 않도록 기존 테이블에 저장.
 */
export async function finalizeBehaviorAiFeedbackAfterSave(input: {
  ok: boolean | void;
  commit?: () => Promise<void>;
  workspaceId: string;
  recordKey: string;
  text?: string;
}): Promise<void> {
  if (!input.ok) return;
  if (isAnnualBehaviorAiFeedbackColumnMissing()) {
    if (input.text !== undefined && input.workspaceId && input.recordKey) {
      await saveRecordAiFeedback(input.workspaceId, input.recordKey, 'behavior', input.text);
    }
    return;
  }
  await input.commit?.();
}
